<template>
  <AppHeaderMobileButton
    v-for="item in navItems"
    :key="item.path"
    :to="item.path"
    :external="item.external || undefined"
    :icon="item.icon"
    :text="item.text"
    :is-active="item.isActive ?? isActive(item.path ?? '')"
    :show-badge="item.showBadge"
   
    @click="item.onClick ? item.onClick($event) : handleCloseMoreMenu(item.path)"
  />

  <AtomTransition variant="fade">
    <div
      v-if="moreMenuVisible"
      class="absolute bottom-full left-0 w-full h-[calc(100dvh-var(--mobile-nav-height))] bg-white z-10 overflow-y-auto space-y-6"
     
    >
      <div class="sticky top-0 z-10 bg-white px-4 py-2">
        <AppHeaderLogo
          :to="localePath({ name: 'index' })"
          size="sm"
          class="flex my-4 justify-center mx-auto w-fit"
          @click="handleCloseMoreMenu(localePath({ name: 'index' }))"
        />

        <MoleculePersona
          v-if="userExists"
          :avatar="{ text: userInitials, src: userAvatar }"
          :name="userFullName"
        >
          <template #sub-name>
            <NuxtLink :to="localePath({ name: 'profile-id', params: { id: userId } })">
              {{ t('viewProfile') }}
            </NuxtLink>
          </template>
        </MoleculePersona>
      </div>

      <div class="rounded-xl bg-white text-base/6 text-gray-900">
        <div class="divide-y divide-gray-100 px-4">
          <TMenuItem
            v-if="userExists"
            :to="localePath({ name: 'dashboard', params: { dashboard: dashboardMode } })"
            :text="t('pageLink.dashboard')"
            size="lg"
            weight="medium"
            show-right-chevron
            :show-badge="dashboardNotificationsTotal > 0"
            @click="handleCloseMoreMenu(localePath({ name: 'dashboard', params: { dashboard: dashboardMode } }))"
          >
            <template #icon>
              <TIconCustom
                name="favicon"
                size="lg"
                fixed-width
              />
            </template>
          </TMenuItem>

          <TMenuItem
            v-if="userHasRv"
            :to="localePath({ name: 'dashboard', params: { dashboard: 'host' } })"
            :text="t('switchToHosting')"
            icon="arrow-down-arrow-up"
            size="lg"
            show-right-chevron
           
            @click="handleCloseMoreMenu(localePath({ name: 'dashboard', params: { dashboard: 'host' } }))"
          />

          <TMenuItem
            v-if="userExists"
            :to="localePath({ name: 'dashboard-profile', params: { dashboard: dashboardMode } })"
            :text="t('pageLink.account')"
            icon="gear"
            size="lg"
            :show-badge="!userIsVerified || !userPhoneNumber"
            show-right-chevron
            @click="handleCloseMoreMenu(localePath({ name: 'dashboard-profile', params: { dashboard: dashboardMode } }))"
          />

          <TMenuItem
            v-if="userExists"
            :to="localePath({ name: 'dashboard-profile-payment', params: { dashboard: dashboardMode } })"
            :text="t('pageLink.paymentMethods')"
            icon="wallet"
            size="lg"
            show-right-chevron
            @click="handleCloseMoreMenu(localePath({ name: 'dashboard-profile-payment', params: { dashboard: dashboardMode } }))"
          />

          <TMenuItem
            :to="t('helpCenter.url')"
            :text="t('helpCenter.title')"
            icon="message-question"
            size="lg"
            external
            show-right-chevron
            @click="handleTrackHelpCenterClicked"
          />

          <TMenuItem
            :to="userExists ? localePath({ name: 'dashboard-rvs-new', params: { dashboard: 'host' } }) : localePath({ name: 'owner' })"
            :text="t('pageLink.listAnRV')"
            icon="rv"
            size="lg"
            external
            show-right-chevron
           
            @click.prevent="handleClickListAnRV"
          />

          <TMenuItem
            v-if="userExists"
            :to="localePath({ name: 'dashboard-profile-notifications', params: { dashboard: dashboardMode } })"
            :text="t('pageLink.notifications')"
            icon="bell"
            size="lg"
            show-right-chevron
            @click="handleCloseMoreMenu(localePath({ name: 'dashboard-profile-notifications', params: { dashboard: dashboardMode } }))"
          />

          <TMenuItem
            :text="t('pageLink.downloadApp')"
            icon="mobile"
            size="lg"
            show-right-chevron
            @click="downloadApp"
          />
        </div>
      </div>

      <div class="divide-y divide-gray-100 px-4 mt-6">
        <TMenuItem
          :text="currentLocale?.name"
          icon="globe"
          size="lg"
          @click="toggleLocale"
        />

        <button
          v-if="userExists"
          type="button"
         
          class="text-base font-semibold underline text-primary-950 rounded-full py-2.5 transition-colors duration-300 ease-in-out hover:bg-gray-50 cursor-pointer"
          @click="logOut()"
        >
          {{ t('actions.logOut') }}
        </button>

        <div
          v-else
          class="py-8 px-4"
        >
          <TButton
            :to="localePath('index-signin')"
            external
            class="w-full"
           
            @click.prevent="showLogin"
          >
            {{ t('auth.login.cta') }}
          </TButton>

          <p class="mt-4 text-center">
            {{ t('auth.login.noAccount') }}

            <NuxtLink
              :to="localePath('index-signup')"
              external
             
              class="font-semibold text-primary-600 underline"
              @click.prevent="showSignup()"
            >
              {{ t('auth.signup.cta') }}
            </NuxtLink>
          </p>
        </div>
      </div>
    </div>
  </AtomTransition>
</template>

<script setup lang="ts">
import type { IconName } from '@fortawesome/fontawesome-svg-core'

const { t } = useI18n()
const localePath = useLocalePath()
const { dashboardMode, isRouteOwnerPage } = useBaseName()
const {
  userAvatar,
  userExists,
  userFullName,
  userInitials,
  userHasRv,
  userIsVerified,
  userPhoneNumber,
  userId,
} = useUser()
const { logOut } = useAuthentication()
const { showLogin } = useLogin()
const { showSignup } = useSignup()
const { downloadApp } = useDownloadApp()
const { currentLocale, toggleLocale } = useAppLocalization()
const { dashboardNotificationsTotal, inboxNotificationsTotal } = useNotificationsData()

const navItems = computed(() => {
  const items: { path?: string, icon: IconName, text: string, external?: boolean, hidden?: boolean, isActive?: boolean, onClick?: (e?: Event) => void, showBadge?: boolean, dataTestid?: string }[] = [
    {
      path: localePath({ name: 'rv-search' }),
      icon: 'magnifying-glass',
      text: t('pageLink.explore'),
    },

    {
      path: localePath({ name: 'dashboard-favourites', params: { dashboard: dashboardMode.value } }),
      icon: 'heart',
      text: t('pageLink.favourites'),
      external: !userExists.value,
      onClick: (e?: Event) => {
        if (!userExists.value && e) {
          e.preventDefault()
          showLogin()
        }
      },
    },

    {
      path: localePath({ name: 'dashboard-trips', params: { dashboard: dashboardMode.value } }),
      icon: 'rv',
      text: t('pageLink.trips'),
      hidden: !userExists.value,
    },

    {
      path: localePath({ name: 'dashboard-messages', params: { dashboard: dashboardMode.value } }),
      icon: 'comment',
      text: t('pageLink.inbox'),
      hidden: !userExists.value,
      showBadge: inboxNotificationsTotal.value > 0,
      dataTestid: 'inbox',
    },

    {
      icon: 'bars',
      text: t('pageLink.more'),
      isActive: moreMenuVisible.value,
      showBadge: userExists.value && (dashboardNotificationsTotal.value > 0 || !userIsVerified.value || !userPhoneNumber.value),
      dataTestid: 'more-menu-toggle-mobile',
      onClick: toggleOpenMoreMenu,
    },
  ]

  return items.filter((item) => !item.hidden)
})

const moreMenuVisible = ref(false)

function toggleOpenMoreMenu() {
  moreMenuVisible.value = !moreMenuVisible.value
}

const route = useRoute()

function handleCloseMoreMenu(path?: string) {
  if (!path || route.path === path) {
    moreMenuVisible.value = false
  }
}

function isActive(path: string) {
  if (moreMenuVisible.value) {
    return false
  }

  return route.path === path
}

watch(route, () => {
  moreMenuVisible.value = false
}, {
  flush: 'post',
})

const { isMdBreakpoint } = useBreakpoint()
const { lock, unlock } = useDocumentScrollLock()
watch(
  [moreMenuVisible, isMdBreakpoint],
  ([newMoreMenuVisible, newIsMdBreakpoint]) => {
    if (newMoreMenuVisible && !newIsMdBreakpoint) {
      lock()
    }
    else {
      unlock()
    }
  },
  { immediate: true },
)

const { pageSource } = usePageSource()
function handleTrackHelpCenterClicked() {
  trackHelpCenterClicked({
    pageSource: pageSource.value,
  })
}

async function handleClickListAnRV() {
  if (userExists.value) {
    handleCloseMoreMenu(localePath({ name: 'dashboard-rvs-new', params: { dashboard: 'host' } }))
    await navigateTo(localePath({ name: 'dashboard-rvs-new', params: { dashboard: 'host' } }))
  }
  else if (isRouteOwnerPage.value) {
    showSignup({ intent: 'ListingIntent' })
  }
  else {
    handleCloseMoreMenu(localePath({ name: 'owner' }))
    await navigateTo(localePath({ name: 'owner' }))
  }
}
</script>

<i18n src="~~/i18n/locales/common/app.json" lang="json" />

<i18n src="~~/i18n/locales/common/auth/signup.json" lang="json" />

<i18n src="~~/i18n/locales/common/auth/login.json" lang="json" />

<i18n lang="json">
  {
    "en": {
      "viewProfile": "View profile"
    },
    "fr": {
      "viewProfile": "Voir le profil"
    }
  }
  </i18n>
